/** Types */
export type LatLngLiteral = google.maps.LatLngLiteral;
type GetMapCoordinatesFromAddress = (address?: string) => Promise<LatLngLiteral | undefined>;
type GetAddressFromCoordinates = (position?: LatLngLiteral) => Promise<string | undefined>;
type GetAddressComponentsFromCoordinates = (position?: LatLngLiteral) => Promise<google.maps.GeocoderAddressComponent[] | undefined>;

/**
 * @description Get Google map coordinates from address
 * - `address` - The address as string.
 */
export const getMapCoordinatesFromAddress: GetMapCoordinatesFromAddress = async (address) => {
  // Return default position if no address
  const position = { lat: 45.9432, lng: 24.9668 };
  if (!address) return position;

  // Get coordinates from address (promise wrapper around callback API)
  const geocoder = new google.maps.Geocoder();
  const { results, status } = await new Promise<{ results: google.maps.GeocoderResult[]; status: google.maps.GeocoderStatus }>((resolve) => {
    void geocoder.geocode({ address }, (res, stat) => resolve({ results: res || [], status: stat as google.maps.GeocoderStatus }));
  });

  if (status === google.maps.GeocoderStatus.OK) {
    const loc = results?.[0]?.geometry?.location;
    const newLat = loc?.lat?.();
    const newLng = loc?.lng?.();
    if (typeof newLat === 'number' && typeof newLng === 'number') {
      position.lat = newLat;
      position.lng = newLng;
    }
  }

  return position;
};

/**
 * @description Get address from Google map coordinates
 * - `position` - The position as LatLngLiteral.
 */
export const getAddressFromCoordinates: GetAddressFromCoordinates = async (position) => {
  if (!position) return undefined;

  const geocoder = new google.maps.Geocoder();
  const { results, status } = await new Promise<{
    results: google.maps.GeocoderResult[];
    status: google.maps.GeocoderStatus;
  }>((resolve) => {
    void geocoder.geocode({ location: position }, (res, stat) => resolve({ results: res || [], status: stat as google.maps.GeocoderStatus }));
  });

  if (status === google.maps.GeocoderStatus.OK) {
    return results?.[0]?.formatted_address;
  }

  return undefined;
};

/**
 * @description Get address components from Google map coordinates
 * - `position` - The position as LatLngLiteral.
 */
export const getAddressComponentsFromCoordinates: GetAddressComponentsFromCoordinates = async (position) => {
  if (!position) return undefined;

  const geocoder = new google.maps.Geocoder();
  const { results, status } = await new Promise<{
    results: google.maps.GeocoderResult[];
    status: google.maps.GeocoderStatus;
  }>((resolve) => {
    void geocoder.geocode({ location: position }, (res, stat) => resolve({ results: res || [], status: stat as google.maps.GeocoderStatus }));
  });

  if (status === google.maps.GeocoderStatus.OK) {
    return results?.[0]?.address_components;
  }

  return undefined;
};
